import React, { useState, useCallback, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useBulkLeaveApi } from './config/index';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import { EMPTY_TARGETS } from '../../components/ui/AssignmentTargetPicker';
import api from '../../services/api';

const Container = styled.div``;

// İstek gövdesi — `BulkLeaveCreateRequest.cs` ile BİREBİR:
// leaveVariationId / leaveStart / leaveEnd / isHourly / tz /
// managerNotes + kapsam listeleri (userIds, departmentIds, locationIds,
// userGroupIds, subTenantIds, includeAll, excludes).
//
// `isPaid` GÖNDERİLMEZ (düzeltme turu 2): BulkLeaveDataAccess artık ücretlilik
// bilgisini İZİN TÜRÜNDEN alıyor (`var isPaid = variation.IsPaid`) ve istemciden
// geleni YOK SAYIYOR — DTO'da alan hâlâ duruyor ama hiçbir şeye yazılmıyor.
// Formdaki onay kutusu, kullanıcının değiştirebildiğini SANDIĞI ölü bir kontroldü
// (kapattığında izin yine ücretli yazılıyordu); kutu da, gövdedeki alan da
// kaldırıldı. Ücretlilik izin türünün özelliğidir, satırın değil.
//
// `cost` de GÖNDERİLMEZ (düzeltme turu 3b): BulkLeaveDataAccess.Create artık
// maliyeti SUNUCUDA hesaplıyor (`MaliyetleriHesaplaAsync` → her kullanıcının
// çalışma takvimine göre) ve `dto.Cost`u satırlara YAZMIYOR — başlığın Cost'u
// bile maliyetler hesaplandıktan SONRA üzerine yazılıyor
// (`bulkLeave.Cost = maliyetler...Max()`). Formdaki sayı alanı kullanıcıya
// kontrolü VARMIŞ gibi gösteriyordu; kaydettiğinde sistem farklı bir rakam
// yazıyordu ve bu rakam ıslak imzalı yıl sonu mutabakat formuna basılıyor.
// Alan da, gövdedeki karşılığı da kaldırıldı; hesaplanan değer liste ve detay
// sayfalarında salt-okunur olarak zaten gösteriliyor.
// Saf fonksiyon: testler formu ekran widget'ları üzerinden sürmek zorunda kalmasın.
//
// NOT: kapsam seçici `titleIds` de üretir ama bu uç onu TANIMIYOR (DTO'da alan yok,
// AssignmentTargetResolver'a da iletilmiyor) — ünvanla hedefleme burada sessizce
// düşer. Backend eksiği; bu dosyada çözülemez.
export const buildBulkLeavePayload = (formData) => ({
  leaveVariationId: formData.leaveVariationId,
  leaveStart: formData.leaveStart,
  leaveEnd: formData.leaveEnd,
  isHourly: !!formData.isHourly,
  managerNotes: formData.managerNotes || '',
  ...(formData.targets || EMPTY_TARGETS),
});

export default function BulkLeavesPage() {
  const { t } = useTranslation();
  const history = useHistory();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  // Oluşturma çekmecesi — uygulamanın standart deseni (obje oluşturma modalda DEĞİL,
  // sağdan açılan GenericFormDrawer'da yapılır).
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [variations, setVariations] = useState([]);

  const { columns, data, fetching, pagination, refresh } = useBulkLeaveApi(queryParams);

  useEffect(() => {
    if (!drawerOpen) return;
    api.get('/v1/leave-variations')
      .then((res) => { const r = res.data?.data; setVariations(Array.isArray(r) ? r : (r?.data || [])); });
  }, [drawerOpen]);

  const variationOptions = useMemo(
    () => variations.map((v) => ({ id: v.id, name: v.name })),
    [variations],
  );

  const schema = useMemo(() => [
    { name: 'leaveVariationId', label: t('bulkLeave.leaveType'), type: 'select', grid: 12, required: true,
      options: variationOptions },
    { name: 'leaveStart', label: t('bulkLeave.leaveStart'), type: 'date', grid: 6, required: true },
    // BİTİŞ ALANININ ANLAMI BİRİME GÖRE DEĞİŞİR (düzeltme turu 4, Bulgu 1).
    // Sunucu sözleşmesi: `leaveEnd` günlük izinde İŞE BAŞLAMA tarihidir, o gün
    // izin DEĞİLDİR (LeaveCalendar.ComputeDailyCost: `returnDate.AddDays(-1)`;
    // LeaveBirthdayWindow.cs da aynı çıkarımı yapar). Tekil izin formu bunu
    // zaten böyle etiketliyor (MyLeavesPage.js — `leave.returnDate` / saatlikte
    // `leave.leaveEnd`); toplu çekmece ise "Bitiş Tarihi" diyordu ve İK izin
    // günlerinin SONUNCUSUNU yazıyordu. Düzeltme turu 3b'de maliyet istemciden
    // alınmayı bırakıp tarihlerden netlenmeye başlayınca bu yanlış okuma
    // doğrudan rakama döndü: tek günlük bir kapanış `Cost = 0`, Pzt–Cum bir
    // kapanış 5 yerine 4 gün yazıyor ve o rakam ISLAK İMZALI yıl sonu
    // mutabakat formuna basılıyor. Etiket düzeltildi — gövdeye gün EKLENMEZ:
    // sözleşme sunucunundur ve saatlik izinde bitiş gerçekten bitiştir.
    { name: 'leaveEnd', type: 'date', grid: 6, required: true,
      label: (f) => (f.isHourly ? t('bulkLeave.leaveEnd') : t('bulkLeave.returnDate')) },
    { name: 'isHourly', label: t('bulkLeave.isHourly'), type: 'checkbox', grid: 6 },
    { name: 'managerNotes', label: t('bulkLeave.managerNotes'), type: 'textarea', grid: 12, rows: 3 },
    { name: 'targets', label: t('bulkLeave.targetSelection'), type: 'assignmentTargets', grid: 12 },
  ], [t, variationOptions]);

  // Kapsam seçici düz bir varsayılan değil iç içe nesne istediği için ilk değerler
  // burada verilir (raffles/RafflesPage.js ile aynı desen).
  const initialValues = useMemo(() => ({
    isHourly: false,
    managerNotes: '',
    targets: { ...EMPTY_TARGETS, excludes: { ...EMPTY_TARGETS.excludes } },
  }), []);

  const handleNewButtonClick = useCallback(() => setDrawerOpen(true), []);

  // GenericFormDrawer truthy dönüşte kapanır; Axios interceptor'ın fırlattığı hata
  // çekmeceyi açık bırakır (kullanıcı girdiğini kaybetmez).
  const handleSave = useCallback(async (formData) => {
    await api.post('/v1/bulk-leaves', buildBulkLeavePayload(formData));
    refresh();
    return true;
  }, [refresh]);

  const handleRowClick = useCallback((row) => {
    history.push(`/bulk-leaves/${row.id}`);
  }, [history]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, columns);
    setQueryParams(params);
  }, [columns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  return (
    <Container>
      {useMemo(() => (
        <DataTable
          title={t('bulkLeave.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={true}
          onNewButtonClick={handleNewButtonClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleRowClick}
        />
      ), [
        t, fetching, columns, data, pagination,
        handleNewButtonClick, filterList,
        handleFilterConfirm, handleDataStateChange, handleRowClick,
      ])}

      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('bulkLeave.new')}
        schema={schema}
        initialValues={initialValues}
        onSave={handleSave}
      />
    </Container>
  );
}
